import { Fragment, useRef, useState } from 'react';

import styled from 'styled-components';

import { Button } from '@components/button';
import { createForm } from '@components/form';
import { HideContainer } from '@components/hide-container';
import { IconAddPlus } from '@components/icons/add-plus';
import {
  ConditionComponent,
  getComparatorIds,
  type Condition as CoreCondition,
} from '@components/integrations';
import { SelectOption } from '@components/select';
import { Separator } from '@components/separator';
import { Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { uuid } from '@utils/uuid';

import {
  Condition,
  ConditionType,
  JourneyActionDetailed,
  JourneyType,
  TravellerJourneyActionDetailed,
} from 'api/types';
import {
  ActionFooter,
  DescriptionFormField,
  useCreateActionFormStore,
  useVariableGroups,
} from 'ui/journey-builder/node-types/common';
import { FormWithUnsavedChangesPrompt } from 'ui/journey-builder/unsaved-changes-prompt';

import { BaseContentProps } from '../common';
import { getFieldMappingId } from '../helpers';

export const conditionGapSize = theme.space[2];

type Data = {
  description: string;
  conditions: Condition[];
  conditionType: ConditionType;
};

export const useConjunctionLabel = (conditionType: ConditionType) => {
  const { t } = useScopedTranslate(locale);
  return conditionType === 'all' ? t('core.and') : t('core.or');
};

const Form = createForm<Data>();

type AvailableActionTypes = 'break_loop' | 'condition_v2';

type ConditionFormContentProps<T extends AvailableActionTypes> = BaseContentProps<
  JourneyActionDetailed<T> | TravellerJourneyActionDetailed<T>
> & {
  onSubmit({
    conditions,
    conditionType,
  }: {
    conditionType: ConditionType;
    conditions: Condition[];
    description?: string;
  }): Promise<any>;
  onCancel(): void;
  journeyId?: JourneyType['shortId'];
  conditionDescription: string;
  hideActionButtons?: boolean;
  showDescriptionField?: boolean;
};

export const ConditionFormContent = <T extends AvailableActionTypes>({
  action,
  onSubmit,
  onCancel,
  journeyId = '',
  conditionDescription,
  hideActionButtons,
  showDescriptionField = false,
}: ConditionFormContentProps<T>) => {
  const { t } = useScopedTranslate(locale);
  const formStore = useCreateActionFormStore();

  const conditionTypes: SelectOption[] = [
    { value: 'all', label: t('all') },
    { value: 'any', label: t('any') },
  ];
  const fieldMappingId = getFieldMappingId(journeyId);
  const initialConditions = useRef(action.values?.conditions ?? []);
  const [conditions, setConditions] = useState<CoreCondition[]>(
    initialConditions.current.map((condition) => ({
      ...condition,
      id: condition.id ?? uuid(),
    }))
  );
  const isDirty = formStore.useIsDirty() || initialConditions.current.length !== conditions.length;

  const baseVariableGroups = useVariableGroups({
    actionVariables: action?.actionVariables,
    journeyId,
    types: [
      'email',
      'text',
      'paragraph',
      'phone',
      'date',
      'separator',
      'boolean',
      'file',
      'select',
      'multi_select',
      'number',
      'decimal',
    ],
  });

  const formValues = formStore.useFormValues();
  const conditionType = formValues.conditionType as ConditionType;

  const handleAddCondition = () => {
    return [
      ...conditions,
      {
        id: uuid(),
        field: { value: '' },
        comparator: undefined,
        value: undefined,
      },
    ];
  };

  const handleSubmit = () => {
    const formConditions = conditions.map((condition) => {
      const { elementId, comparatorId, valueId } = getComparatorIds(condition.id);
      return {
        id: condition.id,
        field: formValues[elementId as keyof typeof formValues],
        comparator: formValues[comparatorId as keyof typeof formValues],
        value: formValues[valueId as keyof typeof formValues],
      } as Condition;
    });
    return onSubmit({
      conditions: formConditions,
      conditionType,
      description: formValues.description as string,
    });
  };

  return (
    <FormWithUnsavedChangesPrompt store={formStore} onSubmit={handleSubmit}>
      <Stack space={6}>
        <HideContainer title={t('actionDetails')}>
          {showDescriptionField ? (
            <DescriptionFormField defaultValue={action.description} />
          ) : (
            <Text font="regular14" text={t('conditionDescription', { conditionDescription })} />
          )}
        </HideContainer>
        <Sidebar.Separator />
        <HideContainer title={t('actionConditions')}>
          <Stack space={6}>
            <Stack orientation="horizontal" justify="space-between" align="center">
              <Text
                font="regular14"
                text={t('conditionDescriptionType', { conditionType, conditionDescription })}
              />
              <SelectContainer>
                <Form.Select
                  label=""
                  id="conditionType"
                  options={conditionTypes}
                  isClearable={false}
                  defaultValue={action.values?.conditionType ?? 'all'}
                />
              </SelectContainer>
            </Stack>
            <Stack space={4}>
              {conditions.map((condition, idx) => (
                <Fragment key={condition.id}>
                  {idx > 0 && (
                    <Stack space={2} orientation="horizontal" justify="center" align="center">
                      <Separator />
                      <Text
                        font="semibold14"
                        style={{ textTransform: 'uppercase' }}
                        color="textSecondary"
                        text={conditionType === 'all' ? t('core.and') : t('core.or')}
                      />
                      <Separator />
                    </Stack>
                  )}
                  <ConditionComponent
                    key={condition.id}
                    variableGroups={baseVariableGroups}
                    onRemove={() => setConditions(conditions.filter((c) => c.id !== condition.id))}
                    setConditions={setConditions}
                    condition={condition}
                    form={Form}
                    index={idx}
                    fieldMappingId={fieldMappingId}
                  />
                </Fragment>
              ))}
              <Stack orientation="horizontal">
                <Button
                  onClick={() => setConditions(handleAddCondition)}
                  text={t('addElementLabel')}
                  iconLeft={<IconAddPlus />}
                  variant="secondary"
                />
              </Stack>
            </Stack>
          </Stack>
        </HideContainer>
        <Sidebar.Separator />
      </Stack>
      <ActionFooter
        action={action}
        isDisabled={!isDirty || conditions.length === 0}
        hideActionButtons={hideActionButtons}
        onCancel={onCancel}
        form={Form}
      />
    </FormWithUnsavedChangesPrompt>
  );
};

const SelectContainer = styled.div({
  width: pxToRem(96),
});

const locale = {
  en: {
    conditionDescription: '{conditionDescription} when specific conditions are met',
    conditionDescriptionType: '{conditionDescription} if {conditionType} of the following matches',
    addElementLabel: 'Add element',
    actionDetails: 'Action details',
    actionConditions: 'Conditions',
    all: 'All',
    any: 'Any',
  },
};
